// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import ChatIcon from '@mui/icons-material/Chat';
import PropTypes from 'prop-types';
import { Badge, Box, Tooltip, Typography } from '@mui/material';

import {
  checkAttributeIsRequired,
  isValueEmpty,
} from '../../../../utils/Utils';
import useTextOverflow from '../../../../hooks/useTextOverFlow';

/**
 * Cell component for name column
 * @param {Object} props - Component props
 * @param {any} props.value - The cell value (name/label)
 * @returns {React.ReactElement} The cell component
 */
const NameCell = ({
  value,
  attribute,
  allAttributeChats,
  handleOpenChat,
  handleAttributeClick,
  isTableView,
}) => {
  const { ref: textRef, isOverflowed } = useTextOverflow(value);

  const openChatBox = useCallback(() => {
    handleOpenChat(allAttributeChats[attribute?.name], attribute);
  }, [allAttributeChats, attribute?.name, handleOpenChat]);

  // Check if field is required based on yup_schema
  const isRequired = useMemo(() => {
    return checkAttributeIsRequired(attribute);
  }, [attribute]);

  const handleClick = useCallback(() => {
    handleAttributeClick(attribute);
  }, [attribute, handleAttributeClick]);

  return (
    <Box
      display="flex"
      flexDirection="row"
      paddingX={isTableView ? '10px' : '0px'}
      gap={1}
      width="100%"
    >
      <Box
        display="inline-flex"
        alignItems="center"
        maxWidth="100%"
        minWidth={0}
      >
        <Tooltip title={typeof value !== 'object' && isOverflowed ? value : ''}>
          <Typography
            ref={textRef}
            variant={isTableView ? 'body2' : 'body4'}
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              maxWidth: '100%',
              ':hover': {
                fontWeight: 600,
                cursor: 'pointer',
              },
            }}
            onClick={handleClick}
          >
            {value || ''}
          </Typography>
        </Tooltip>

        {isRequired && (
          <Typography
            component="span"
            variant="body8"
            color="error.main"
            sx={{
              marginLeft: '2px',
              flexShrink: 0,
              lineHeight: 1,
            }}
          >
            *
          </Typography>
        )}
      </Box>

      {!isValueEmpty(allAttributeChats[attribute?.name]) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={openChatBox}
        >
          <Badge
            badgeContent={allAttributeChats[attribute?.name]?.length}
            sx={{
              '& .MuiBadge-badge': {
                marginRight: '0px',
                marginTop: '0px',
              },
            }}
          >
            <ChatIcon color="primary" />
          </Badge>
        </Box>
      )}
    </Box>
  );
};

NameCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  attribute: PropTypes.shape({
    name: PropTypes.string,
    is_mandatory: PropTypes.bool,
  }),
  allAttributeChats: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)])
    .isRequired,
  handleOpenChat: PropTypes.func.isRequired,
  handleAttributeClick: PropTypes.func.isRequired,
  isTableView: PropTypes.bool,
};

/**
 * Cell renderer for the Name column
 * @param {Object} props - Cell props from React Table
 * @param {any} props.value - The cell value
 * @returns {React.ReactElement} The rendered cell
 */
const NameCellRenderer = (
  props,
  allAttributeChats,
  handleOpenChat,
  handleAttributeClick,
  isTableView,
  mandatoryConstraints
) => {
  const { row, value } = props;

  // Inject is_mandatory from dynamic constraints onto the attribute
  const attribute = {
    ...(row?.original || {}),
    ...(mandatoryConstraints?.[row?.original?.id] !== undefined &&
      mandatoryConstraints?.[row?.original?.id] !== null && {
        is_mandatory:
          mandatoryConstraints[row?.original?.id]?.is_mandatory === true,
      }),
  };

  return (
    <NameCell
      value={value}
      attribute={attribute}
      allAttributeChats={allAttributeChats}
      handleOpenChat={handleOpenChat}
      handleAttributeClick={handleAttributeClick}
      isTableView={isTableView}
    />
  );
};

NameCellRenderer.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
};

export default NameCellRenderer;
